import { useEffect, useRef, useState } from "react";
import { Skel } from "@/components/home/HomeStrips";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Ticket, Tag, Instagram, ArrowUpRight } from "lucide-react";
import { useInView } from "@/lib/useInView";
import { api, resolveImageUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { entryImage } from "@/lib/placeholders";
import { entryPath } from "@/lib/paths";

// patch 30: rise-in when the card first comes on screen (CSS instead of framer-motion)
function Rise({ i, className = "", children, ...rest }) {
  const ref = useRef(null);
  const seen = useInView(ref, { once: true, rootMargin: "0px 0px -10% 0px" });
  return (
    <div ref={ref} className={`tier-rise ${seen ? "in" : ""} ${className}`} style={{ animationDelay: `${i * 80}ms` }} {...rest}>
      {children}
    </div>
  );
}

export function fmtDate(iso, lang) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(lang === "pt" ? "pt-PT" : "en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function daysLeft(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const now = new Date();
  d.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);
  return Math.round((d - now) / 86400000);
}

export function countdownLabel(iso, lang) {
  const dl = daysLeft(iso);
  if (dl == null || dl < 0) return null;
  if (dl === 0) return lang === "pt" ? "É hoje!" : "Happening today!";
  if (dl === 1) return lang === "pt" ? "Falta 1 dia" : "1 day to go";
  return lang === "pt" ? `Faltam ${dl} dias` : `${dl} days to go`;
}

export function TicketLabel(t, type) {
  return { online: t.upcoming.t_online, inperson: t.upcoming.t_inperson, both: t.upcoming.t_both }[type] || "";
}

export default function UpcomingEvents() {
  const { t, lang } = useI18n();
  const [events, setEvents] = useState(null);
  const [active, setActive] = useState(0);
  const [hint, setHint] = useState(() => {
    try {
      return !localStorage.getItem("tier_swipe_seen");
    } catch {
      return true;
    }
  });
  const row = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get("/upcoming-events").then((r) => alive && setEvents(r.data || [])).catch(() => alive && setEvents([]));
    return () => {
      alive = false;
    };
  }, []);

  // patch 28: on phones the cards scroll sideways; the dots follow the card in view
  const onScroll = () => {
    const el = row.current;
    if (!el || !el.children.length) return;
    const w = el.children[0].getBoundingClientRect().width + 16;
    setActive(Math.round(el.scrollLeft / w));
    if (hint && el.scrollLeft > 20) {
      setHint(false);
      try {
        localStorage.setItem("tier_swipe_seen", "1");
      } catch {
        /* storage blocked */
      }
    }
  };

  if (events === null)
    return (
      <section className="py-12 border-t border-amber-500/15" data-testid="upcoming-loading">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <Skel className="h-3 w-28" />
          <Skel className="h-9 w-56 mt-3" />
          <div className="flex gap-4 mt-6 overflow-hidden">
            {[0, 1, 2].map((k) => <Skel key={k} className="h-[300px] w-[82vw] max-w-[340px] shrink-0" />)}
          </div>
        </div>
      </section>
    );
  if (!events.length) return null;

  return (
    <section
      data-testid="upcoming-events"
      id="upcoming-events"
      className="relative py-16 lg:py-24 border-t border-amber-500/15 bg-gradient-to-b from-[#0b0b0d] to-[#080809] overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"
      />
      <div
        aria-hidden
        className="absolute -top-24 right-0 w-[520px] h-[520px] rounded-full bg-amber-500/[0.06] blur-3xl pointer-events-none"
      />
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 relative">
        <div className="text-center sm:text-left mb-10">
          <div className="text-[11px] uppercase tracking-[0.35em] font-bold text-amber-400 inline-flex items-center gap-2">
            <CalendarDays className="w-4 h-4" /> {t.upcoming.overline}
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mt-2">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-stone-100 leading-[0.95] flex items-center justify-center sm:justify-start gap-3 sm:gap-4">
              {t.upcoming.title}
              <span className="px-sprite px-calendar" style={{ backgroundImage: "url(/pixo/calendar-sheet.png)" }} aria-hidden data-testid="events-calendar" />
            </h2>
            <p className="text-sm sm:text-base text-stone-400 max-w-md leading-relaxed">{t.upcoming.lede}</p>
          </div>
        </div>

        <div
          ref={row}
          onScroll={onScroll}
          data-testid="upcoming-row"
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-5 px-5 pb-1 md:mx-0 md:px-0 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-5 md:overflow-visible"
        >
          {events.map((e, i) => (
            <Rise
              key={e.id}
              i={i}
              data-testid={`upcoming-event-${e.id}`}
              className="group panel rounded-sm overflow-hidden border-amber-500/25 shadow-[0_0_40px_-24px_rgba(212,168,44,0.6)] hover:border-amber-400/60 transition-colors shrink-0 w-[82vw] max-w-[340px] snap-start md:w-auto md:max-w-none"
            >
              <div className="relative h-36 overflow-hidden">
                <img
                  src={resolveImageUrl(entryImage(e), 400)}
                  alt={e.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0d] via-transparent to-transparent" />
                {e.next_date && (
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-amber-500 text-black text-[10px] font-black uppercase tracking-[0.15em]">
                    <CalendarDays className="w-3 h-3" /> {fmtDate(e.next_date, lang)}
                  </div>
                )}
                {countdownLabel(e.next_date, lang) && (
                  <div
                    data-testid={`upcoming-countdown-${e.id}`}
                    className="absolute top-12 left-3 md:top-3 md:left-auto md:right-3 inline-flex items-center px-2.5 py-1 rounded-sm bg-black/75 border border-amber-400/50 text-amber-300 text-[10px] font-black uppercase tracking-[0.12em] animate-pulse"
                  >
                    {countdownLabel(e.next_date, lang)}
                  </div>
                )}
              </div>
              <div className="p-5">
                <Link
                  to={entryPath(e)}
                  className="font-display text-xl font-bold uppercase tracking-tight text-stone-100 hover:text-amber-200 transition-colors inline-flex items-center gap-1"
                >
                  {e.name}
                </Link>
                <div className="mt-3 space-y-1.5 text-xs text-stone-400">
                  {(e.venue || e.location) && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-amber-400/80 shrink-0" /> {e.venue || e.location}
                    </div>
                  )}
                  {e.entry_price && (
                    <div className="flex items-center gap-2">
                      <Tag className="w-3.5 h-3.5 text-amber-400/80 shrink-0" /> {t.upcoming.price}: {e.entry_price}
                    </div>
                  )}
                  {e.ticket_type && (
                    <div className="flex items-center gap-2">
                      <Ticket className="w-3.5 h-3.5 text-amber-400/80 shrink-0" /> {TicketLabel(t, e.ticket_type)}
                    </div>
                  )}
                </div>
                {e.instagram && (
                  <a
                    href={e.instagram}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`upcoming-instagram-${e.id}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-amber-300 hover:text-amber-200"
                  >
                    <Instagram className="w-3.5 h-3.5" /> {t.upcoming.follow} <ArrowUpRight className="w-3 h-3" />
                  </a>
                )}
              </div>
            </Rise>
          ))}
        </div>
        {events.length > 1 && (
          <div className="md:hidden flex items-center justify-center gap-2 mt-4" aria-hidden>
            {events.map((e, i) => (
              <span key={e.id} className={`h-1.5 rounded-full transition-all duration-300 ${i === Math.min(active, events.length - 1) ? "w-5 bg-amber-400" : "w-1.5 bg-stone-600"}`} />
            ))}
            {hint && <span className="ml-2 text-[12px] text-amber-300/80 animate-pulse">{t.h27.swipe}</span>}
          </div>
        )}
      </div>
    </section>
  );
}
